import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  FileArrowDown, WarningCircle, Buildings, Envelope,
} from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// View-only SOW review page. The customer reads the SOW online and receives the
// full package (SOW + Master Agreement) via Outlook to physically sign — no
// digital signature is captured here. First view is recorded server-side and
// mirrored to PSDoc as `sow_viewed_by_client`.
export default function SignSow() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/public/sign/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "This link is invalid or expired."));
  }, [token]);

  if (error && !data) {
    return (
      <Frame>
        <div className="text-center py-12">
          <WarningCircle size={48} className="text-red-500 mx-auto mb-3" />
          <div className="text-xl font-semibold text-slate-800">{error}</div>
          <div className="text-sm text-slate-500 mt-2">If you believe this is a mistake, please contact your SoverenX Systems representative.</div>
        </div>
      </Frame>
    );
  }
  if (!data) return <Frame><div className="text-center py-12 text-slate-400">Loading…</div></Frame>;

  const sec = data.sections || {};

  return (
    <Frame>
      <div className="space-y-6" data-testid="sign-page">
        {/* Header */}
        <div className="border-b border-slate-200 pb-4">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-xs font-mono uppercase tracking-widest text-blue-600">Statement of Work · Review Copy</div>
            {data.ps_code && (
              <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border border-blue-300 bg-blue-50 text-blue-800" title="Deal reference — please quote this in any emails.">
                ref · {data.ps_code}
              </span>
            )}
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mt-1">{data.project_name}</h1>
          <div className="flex items-center gap-3 mt-2 text-sm text-slate-600">
            <span className="flex items-center gap-1"><Buildings size={14} /> {data.client_name}</span>
            <span>·</span>
            <span className="font-mono">${Number(data.value || 0).toLocaleString()} AUD</span>
            {data.start_date && data.end_date && (
              <>
                <span>·</span>
                <span>{data.start_date} → {data.end_date}</span>
              </>
            )}
          </div>
        </div>

        {/* Signing instructions — replaces the old digital sign form */}
        <div className="border-2 border-blue-200 bg-blue-50/60 rounded-lg p-5" data-testid="signing-instructions">
          <div className="flex items-start gap-3">
            <Envelope size={22} className="text-blue-600 shrink-0 mt-0.5" />
            <div className="space-y-2">
              <div className="text-lg font-semibold text-slate-900">How to accept this SOW</div>
              <p className="text-sm text-slate-700">
                Please review the Statement of Work below alongside the <b>SOW PDF</b> and <b>Master Services Agreement</b> we've emailed you.
                Both documents need to be physically signed and returned via email to your SoverenX Systems representative to formalise the engagement.
              </p>
              <p className="text-xs text-slate-500 italic">
                This page is for online review only — no digital signature is captured here.
              </p>
            </div>
          </div>
        </div>

        {/* SOW body */}
        {sec.solution_overview && (
          <Section title="Solution overview">
            <p className="whitespace-pre-wrap text-sm text-slate-700 leading-relaxed">{sec.solution_overview}</p>
          </Section>
        )}
        {sec.scope_items?.length > 0 && (
          <Section title="Scope of work">
            <ul className="text-sm text-slate-700 list-disc list-inside space-y-1">
              {sec.scope_items.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          </Section>
        )}
        {sec.deliverables?.length > 0 && (
          <Section title="Deliverables">
            <ul className="text-sm text-slate-700 list-disc list-inside space-y-1">
              {sec.deliverables.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          </Section>
        )}
        {sec.out_of_scope?.length > 0 && (
          <Section title="Out of scope">
            <ul className="text-sm text-slate-700 list-disc list-inside space-y-1">
              {sec.out_of_scope.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          </Section>
        )}
        {sec.assumptions?.length > 0 && (
          <Section title="Assumptions">
            <ul className="text-sm text-slate-700 list-disc list-inside space-y-1">
              {sec.assumptions.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          </Section>
        )}
        {sec.dependencies?.length > 0 && (
          <Section title="Dependencies">
            <ul className="text-sm text-slate-700 list-disc list-inside space-y-1">
              {sec.dependencies.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          </Section>
        )}
        {/* --- Brown-bench extras (Feb 2026): shown only when PSDoc includes them --- */}
        {sec.rollback_plan?.length > 0 && (
          <Section title="Rollback plan">
            <ul className="text-sm text-slate-700 list-disc list-inside space-y-1">
              {sec.rollback_plan.map((s, i) => <li key={i}>{typeof s === "string" ? s : (s?.text || JSON.stringify(s))}</li>)}
            </ul>
          </Section>
        )}
        {sec.cutover_checklist?.length > 0 && (
          <Section title="Cutover checklist">
            <div className="space-y-3">
              {sec.cutover_checklist.map((phase, i) => {
                // Accept either { phase, items[] } objects OR plain strings for a flat list
                if (typeof phase === "string") return <div key={i} className="text-sm text-slate-700">• {phase}</div>;
                const items = Array.isArray(phase?.items) ? phase.items : [];
                return (
                  <div key={i} className="border-l-2 border-blue-300 pl-3">
                    <div className="text-[11px] font-mono uppercase tracking-widest text-blue-700 mb-1">{phase?.phase || `Phase ${i+1}`}</div>
                    <ul className="text-sm text-slate-700 list-disc list-inside space-y-0.5">
                      {items.map((it, j) => <li key={j}>{typeof it === "string" ? it : (it?.text || JSON.stringify(it))}</li>)}
                    </ul>
                  </div>
                );
              })}
            </div>
          </Section>
        )}
        {data.attachments?.length > 0 && (
          <Section title="Supporting documents">
            <div className="flex flex-wrap gap-2">
              {data.attachments.map((a, i) => (
                <a
                  key={i} href={a.url} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 border border-slate-300 rounded px-3 py-1.5 text-sm hover:border-blue-400 hover:text-blue-700"
                >
                  <FileArrowDown size={14} /> {(a.kind || "").toUpperCase()} — {a.filename}
                </a>
              ))}
            </div>
          </Section>
        )}

        {/* Footer contact block */}
        <div className="border-t border-slate-200 pt-4 text-xs text-slate-500 text-center">
          Questions? Reply to your SoverenX Systems email thread or contact <a className="text-blue-600 hover:underline" href="mailto:info@soverenxsystems.com.au">info@soverenxsystems.com.au</a>.
        </div>
      </div>
    </Frame>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500 mb-2">{title}</div>
      {children}
    </div>
  );
}

function Frame({ children }) {
  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-3xl mx-auto px-4">
        <div className="flex items-center justify-center mb-6">
          <img src="/soverenx-logo.jpg" alt="SoverenX Systems" className="h-14 w-auto rounded bg-black px-2" />
        </div>
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6 md:p-8">
          {children}
        </div>
        <div className="text-center text-xs text-slate-400 mt-6">
          SoverenX Systems · info@soverenxsystems.com.au
        </div>
      </div>
    </div>
  );
}
